import type * as hast from 'hast';
import pMap from 'p-map';
import { find } from 'unist-util-find';
import { EXIT, visit } from 'unist-util-visit';

import {
    type CardsImageObjectFit,
    type ContentRef,
    DOCUMENT_TABLE_CELL_SPAN_MAXIMUM,
    DOCUMENT_TABLE_CELL_SPAN_MINIMUM,
    type DocumentTableSelectOption,
    VerticalAlignment,
} from '@gitbook/api-client';
import {
    BLOCK_TABLE,
    type BlockTableData,
    FRAGMENT_TABLE_CELL,
    paragraph,
    table,
    type TableCellMerge,
    type TableColumnDefinition,
    type TableTextColumnVerticalAlignment,
    type TableView,
} from '@gitbook/doc-core';
import { Block, Fragment, generateKey } from '@gitbook/slate';
import { createOrderIndex } from '@gitbook/util-orderindex';
import { assertNever, filterOutNullable } from '@gitbook/util-ts';

import type { ParsingOptions } from '../options';
import type { HastToGastTransformMap } from './types';
import {
    getBooleanAttribute,
    getEnumAttribute,
    getNumberAttribute,
    getStringAttribute,
} from './utils';

export const transformBlockTables: HastToGastTransformMap = {
    table: async (node: hast.Element, eat, context, parsingContext) => {
        // First row will either be in the thead or the tbody
        const { firstRow, isOnlyRow } = getFirstTableRow(node);

        // A table with no rows does not produce a block.
        if (!firstRow) {
            return [];
        }

        // Test if the first row should be processed as a normal row or as a header
        const useFirstRowAsHeader =
            !isOnlyRow &&
            Boolean(
                // We consider it a header if it doesn't contain formatting
                !find(
                    firstRow,
                    // @ts-expect-error
                    ({ type, tagName }) =>
                        type !== 'text' && tagName !== 'td' && tagName !== 'th' && tagName !== 'tr'
                ) ||
                // Or if we can find a th with a select tag in it
                find(
                    firstRow,
                    (child: hast.Element) =>
                        child.type === 'element' &&
                        child.tagName === 'th' &&
                        Boolean(getSelectFromHeaderColumn(child))
                )
            );

        const fragments: Fragment[] = [];
        const logicalRows = extractLogicalTableRows(node);
        const data: BlockTableData = {
            records: {},
            definition: {},
            view: extractTableView(node, { useFirstRowAsHeader }),
            ...(typeof node.properties?.dataFullWidth !== 'undefined'
                ? { fullWidth: node.properties.dataFullWidth !== 'false' }
                : undefined),
            ...(typeof node.properties?.dataSearch !== 'undefined'
                ? { search: node.properties.dataSearch !== 'false' }
                : undefined),
        };

        // Extract column definitions
        const orderedColumns = extractColumnKeys(logicalRows);
        await forEachColumn(logicalRows[0], orderedColumns, (cell, colId) => {
            const td = cell?.element ?? null;
            if (td && useFirstRowAsHeader) {
                const colDef = extractHeaderColumnDefinition(colId, td);
                data.definition[colDef.id] = colDef;
                data.view = extractTableViewForColumn(colId, td, data.view);
            } else {
                data.definition[colId] = {
                    id: colId,
                    type: 'text',
                    title: '',
                    textAlignment: getEnumAttribute(
                        td?.properties,
                        'align',
                        ['left', 'center', 'right'],
                        'left'
                    ),
                    verticalAlignment: getEnumAttribute<TableTextColumnVerticalAlignment>(
                        td?.properties,
                        'vAlign',
                        [VerticalAlignment.Top, VerticalAlignment.Middle, VerticalAlignment.Bottom],
                        undefined
                    ),
                };
                data.view.columns.push(colId);
            }
        });

        let lastOrderIndex: string | undefined;
        const rowRecordIds: Array<string | undefined> = [];
        for (const [rowIndex, row] of logicalRows.entries()) {
            // We skip the first row if it was already processed
            if (rowIndex === 0 && useFirstRowAsHeader) {
                continue;
            }

            const recordId = generateKey();
            rowRecordIds[rowIndex] = recordId;

            data.records[recordId] = {
                orderIndex: createOrderIndex(lastOrderIndex ? { before: lastOrderIndex } : {}),
                values: {},
            };

            lastOrderIndex = data.records[recordId].orderIndex;

            await forEachColumn(row, orderedColumns, async (cell, colId) => {
                const td = cell?.element ?? null;
                const colDef = data.definition[colId];

                switch (colDef.type) {
                    case 'text': {
                        const nodes = td ? await eat('blocks', td.children, context) : [];
                        const fragment = Fragment.create({
                            fragment: generateKey(),
                            nodes: nodes.length > 0 ? nodes : [paragraph.createParagraph()],
                            type: FRAGMENT_TABLE_CELL,
                        });

                        fragments.push(fragment);

                        data.records[recordId].values[colId] = fragment.fragmentKey;
                        break;
                    }

                    case 'number':
                    case 'rating':
                        data.records[recordId].values[colId] = td ? extractNumberValue(td) : null;
                        break;

                    case 'checkbox':
                        data.records[recordId].values[colId] = td
                            ? extractNodeText(td) !== 'false'
                            : false;
                        break;

                    case 'select':
                        data.records[recordId].values[colId] = td ? extractNodeOptions(td) : [];
                        break;

                    case 'files': {
                        const refs = td
                            ? await extractContentReferences(td, parsingContext, true)
                            : [];
                        data.records[recordId].values[colId] = refs
                            .map((ref) => (ref.kind === 'file' ? ref.file : null))
                            .filter(filterOutNullable);
                        break;
                    }
                    case 'users': {
                        const refs = td ? await extractContentReferences(td, parsingContext) : [];
                        data.records[recordId].values[colId] = refs
                            .map((ref) => (ref.kind === 'user' ? ref.user : null))
                            .filter(filterOutNullable);
                        break;
                    }
                    case 'content-ref': {
                        const refs = td ? await extractContentReferences(td, parsingContext) : [];
                        data.records[recordId].values[colId] = refs[0] || null;
                        break;
                    }
                    case 'image': {
                        const refs = td
                            ? await extractContentReferences(td, parsingContext, true)
                            : [];
                        const contentRef = refs[0] || null;

                        // Filter to only file/URL refs (image columns don't support page refs)
                        const imageRef =
                            contentRef && (contentRef.kind === 'file' || contentRef.kind === 'url')
                                ? contentRef
                                : null;

                        // Check if this is a cover column with object-fit
                        const objectFit = td
                            ? getEnumAttribute(td.properties, 'dataObjectFit', [
                                  'contain',
                                  'fill',
                                  'cover',
                              ])
                            : undefined;
                        const alt = td ? getStringAttribute(td.properties, 'dataAlt') : undefined;

                        if (imageRef && (objectFit || alt)) {
                            data.records[recordId].values[colId] = {
                                ref: imageRef,
                                ...(objectFit
                                    ? { objectFit: objectFit as CardsImageObjectFit }
                                    : {}), // Store as new format with objectFit
                                ...(alt ? { alt } : {}),
                            };
                        } else {
                            data.records[recordId].values[colId] = imageRef; // Store as legacy format
                        }
                        break;
                    }
                    default:
                        assertNever(colDef);
                }
            });
        }

        // Merges resolve against the visible column order, so a span crossing a hidden column
        // would silently cover (and clear) the next visible cell instead. Import it unmerged.
        const visibleColumnIds = new Set(data.view.columns);

        for (const [rowIndex, row] of logicalRows.entries()) {
            if (!rowRecordIds[rowIndex]) continue;
            for (const [columnIndex, cell] of row.entries()) {
                if (!cell || (cell.colSpan < 2 && cell.rowSpan < 2)) {
                    continue;
                }

                const columns = orderedColumns.slice(columnIndex, columnIndex + cell.colSpan);
                const records = rowRecordIds.slice(rowIndex, rowIndex + cell.rowSpan);
                if (
                    columns.length === cell.colSpan &&
                    records.length === cell.rowSpan &&
                    records.every((record): record is string => typeof record === 'string') &&
                    columns.every((columnId) => visibleColumnIds.has(columnId)) &&
                    table.getMergeEligibility(data, { records, columns }).eligible
                ) {
                    const merge: TableCellMerge =
                        records.length === 1
                            ? {
                                  anchor: { record: records[0]!, column: columns[0]! },
                                  rowSpan: 1,
                                  colSpan: columns.length,
                              }
                            : {
                                  anchor: { record: records[0]!, column: columns[0]! },
                                  rowSpan: records.length,
                                  colSpan: 1,
                              };
                    data.cellMerges = [...(data.cellMerges ?? []), merge];
                }
            }
        }

        return [
            Block.createVoid({
                type: BLOCK_TABLE,
                data,
                fragments,
            }),
        ];
    },
};

/**
 * Convert a unified node into a string.
 */
function extractNodeText(node: hast.Element): string {
    let result = '';

    visit(
        node,
        (node) => node.type === 'text',
        (text) => {
            // @ts-ignore
            result += text.value;
        }
    );

    return result.trim();
}

/**
 * Single pass: get first <tr> and detect if the table has exactly one row.
 * Works for rows directly inside a table, thead, tbody, or tfoot.
 */
export function getFirstTableRow(table: hast.Element) {
    let firstRow: hast.Element | undefined;
    let seen = 0;

    visit(
        table,
        // Check whether this is a <tr> whose parent is a supported table row group
        (n: hast.Node, _index?: number, parent?: hast.Parent): boolean => {
            // check node is an element
            if (n.type !== 'element') {
                return false;
            }

            // check node is a <tr>
            const el = n as hast.Element;
            if (el.tagName !== 'tr') {
                return false;
            }

            // check parent exists and is an element
            if (!parent || (parent as hast.Node).type !== 'element') {
                return false;
            }

            // check the parent is a supported table row group
            if (!isRowParent(parent)) {
                return false;
            }

            return true;
        },
        // visitor: record first row and stop after finding a second
        (tr: hast.Element) => {
            if (!firstRow) {
                firstRow = tr;
            }
            seen += 1;
            if (seen > 1) {
                return EXIT;
            }
        }
    );

    return { firstRow, isOnlyRow: seen === 1 };
}

/**
 * Determines whether the given node can directly contain table rows.
 */
function isRowParent(node: hast.Node): node is hast.Element {
    return (
        node.type === 'element' &&
        ['table', 'thead', 'tbody', 'tfoot'].includes((node as hast.Element).tagName)
    );
}

/**
 * Parse all data-option spans from a node.
 */
function extractNodeOptions(node: hast.Element): string[] | false {
    const result: string[] = [];

    visit(
        node,
        (node) => {
            // @ts-ignore
            return node.tagName === 'span' && 'dataOption' in node.properties;
        },
        (optionSpan) => {
            // @ts-ignore
            result.push(optionSpan.properties.dataOption);
        }
    );

    return result;
}

/**
 * Iterate over all columns of a row.
 */
async function forEachColumn(
    row: LogicalTableCell[],
    columnKeys: string[],
    callback: (cell: LogicalTableCell | null, colId: string, index: number) => Promise<void> | void
) {
    for (let index = 0; index < columnKeys.length; index += 1) {
        await callback(row[index] || null, columnKeys[index], index);
    }
}

/**
 * Extract a list of column keys from the table content.
 * It iterates through all the logical rows to find the row with the most columns.
 */
function extractColumnKeys(rows: LogicalTableCell[][]): string[] {
    const maxColumns = Math.max(...rows.map((row) => row.length));
    return new Array(maxColumns).fill(0).map(() => generateKey());
}

export interface LogicalTableCell {
    element: hast.Element | null;
    colSpan: number;
    rowSpan: number;
}

/** Expand HTML cell spans into the logical grid consumed by the document table model. */
export function extractLogicalTableRows(table: hast.Element): LogicalTableCell[][] {
    const rows: Array<{ element: hast.Element; group: hast.Element }> = [];
    visit(
        table,
        (node: hast.Node, _index?: number, parent?: hast.Parent) =>
            node.type === 'element' &&
            (node as hast.Element).tagName === 'tr' &&
            Boolean(parent && isRowParent(parent)),
        (row: hast.Element, _index, parent) => {
            if (parent && isRowParent(parent)) {
                rows.push({ element: row, group: parent });
            }
        }
    );

    const remainingRowsByGroup = new Map<hast.Element, number>();
    for (const { group } of rows) {
        remainingRowsByGroup.set(group, (remainingRowsByGroup.get(group) ?? 0) + 1);
    }
    const rowSpansByGroup = new Map<hast.Element, Map<number, number>>();

    // A span may not widen the table past what its real cells already do. The floor covers a
    // table whose rows are entirely spanned, where no row declares the real column count.
    const rowCells = rows.map(({ element }) => element.children.filter(isTableColumn));
    const maxColumns = Math.max(
        DOCUMENT_TABLE_CELL_SPAN_MAXIMUM,
        ...rowCells.map((cells) => cells.length)
    );

    return rows.map(({ group }, rowIndex) => {
        const remainingRowsInGroup = remainingRowsByGroup.get(group) ?? 1;
        remainingRowsByGroup.set(group, remainingRowsInGroup - 1);
        const rowSpans = rowSpansByGroup.get(group) ?? new Map<number, number>();
        rowSpansByGroup.set(group, rowSpans);
        const logicalRow: LogicalTableCell[] = [];

        for (const [columnIndex, remainingRows] of rowSpans) {
            logicalRow[columnIndex] = { element: null, colSpan: 1, rowSpan: 1 };
            if (remainingRows <= 1) {
                rowSpans.delete(columnIndex);
            } else {
                rowSpans.set(columnIndex, remainingRows - 1);
            }
        }

        const cells = rowCells[rowIndex];
        let columnIndex = 0;
        for (const [cellIndex, cell] of cells.entries()) {
            while (logicalRow[columnIndex]) {
                columnIndex += 1;
            }

            const remainingCells = cells.length - cellIndex - 1;
            const colSpan = Math.max(
                DOCUMENT_TABLE_CELL_SPAN_MINIMUM,
                Math.min(getCellSpan(cell, 'colSpan'), maxColumns - columnIndex - remainingCells)
            );
            const rowSpan = getCellSpan(cell, 'rowSpan', remainingRowsInGroup);
            logicalRow[columnIndex] = { element: cell, colSpan, rowSpan };

            for (let offset = 1; offset < colSpan; offset += 1) {
                logicalRow[columnIndex + offset] = { element: null, colSpan: 1, rowSpan: 1 };
            }

            if (rowSpan > 1) {
                for (let offset = 0; offset < colSpan; offset += 1) {
                    rowSpans.set(columnIndex + offset, rowSpan - 1);
                }
            }

            columnIndex += colSpan;
        }

        return logicalRow;
    });
}

function getCellSpan(
    cell: hast.Element,
    property: 'colSpan' | 'rowSpan',
    remainingRowsInGroup = 1
): number {
    const value = getNumberAttribute(cell.properties, property, DOCUMENT_TABLE_CELL_SPAN_MINIMUM);
    if (property === 'rowSpan' && value === 0) {
        return Math.min(remainingRowsInGroup, DOCUMENT_TABLE_CELL_SPAN_MAXIMUM);
    }
    const validatedSpan =
        Number.isInteger(value) && value >= DOCUMENT_TABLE_CELL_SPAN_MINIMUM
            ? Math.min(value, DOCUMENT_TABLE_CELL_SPAN_MAXIMUM)
            : DOCUMENT_TABLE_CELL_SPAN_MINIMUM;
    return property === 'rowSpan' ? Math.min(validatedSpan, remainingRowsInGroup) : validatedSpan;
}

/**
 * Test if an element is a table column.
 */
function isTableColumn(element: hast.Element | hast.Comment | hast.Text): element is hast.Element {
    return element.type === 'element' && (element.tagName === 'td' || element.tagName === 'th');
}

/**
 * Extract table view from the table node.
 */
function extractTableView(
    table: hast.Element,
    options: { useFirstRowAsHeader: boolean }
): TableView {
    const viewType = table.properties?.dataView || 'grid';

    switch (viewType) {
        case 'cards': {
            return {
                type: 'cards',
                columns: [],
                cardSize: getEnumAttribute<'medium' | 'large', 'medium'>(
                    table.properties,
                    'dataCardSize',
                    ['large', 'medium'],
                    'medium'
                ),
                hideColumnTitle: 'dataColumnTitleHidden' in (table.properties || {}),
                // Absence means the wrapping default; only materialize the carousel opt-out.
                ...(table.properties?.dataCardWrap === 'false' ? { wrap: false } : {}),
            };
        }
        default: {
            return {
                type: 'grid',
                columns: [],
                useNewSizing: true,
                hideHeader:
                    getBooleanAttribute(table.properties, 'dataHeaderHidden') ||
                    !options.useFirstRowAsHeader,
                stickyHeader: getBooleanAttribute(table.properties, 'dataHeaderSticky'),
                ...(getBooleanAttribute(table.properties, 'dataFirstColumnSticky')
                    ? { stickyFirstColumn: true }
                    : {}),
            };
        }
    }
}

/**
 * Extract table view parameters from a column node (td).
 */
function extractTableViewForColumn<V extends TableView>(
    colId: string,
    td: hast.Element,
    view: V
): V {
    switch (view.type) {
        case 'cards': {
            const hidden = getBooleanAttribute(td.properties, 'dataHidden');
            const isCover = getBooleanAttribute(td.properties, 'dataCardCover');
            const isTarget = getBooleanAttribute(td.properties, 'dataCardTarget');
            const isCoverDark = getBooleanAttribute(td.properties, 'dataCardCoverDark');

            return {
                ...view,
                ...(isCover ? { coverDefinition: colId } : {}),
                ...(isCoverDark ? { coverDefinitionDark: colId } : {}),
                ...(isTarget ? { targetDefinition: colId } : {}),
                columns: hidden ? view.columns : [...view.columns, colId],
            };
        }
        case 'grid': {
            const hidden = getBooleanAttribute(td.properties, 'dataHidden');
            const columnWidth = getNumberAttribute(td.properties, 'width');
            const viewColumnWidths = view.columnWidths ?? {};

            return {
                ...view,
                columns: hidden ? view.columns : [...view.columns, colId],
                columnWidths: {
                    ...viewColumnWidths,
                    ...(columnWidth ? { [colId]: columnWidth } : {}),
                },
            };
        }
        default:
            assertNever(view);
    }
}

/**
 * Extract table definition from a first row TD.
 */
function extractHeaderColumnDefinition(id: string, td: hast.Element): TableColumnDefinition {
    const type = td.properties?.dataType || 'text';
    const title = extractNodeText(td);

    const selectTag = getSelectFromHeaderColumn(td);
    if (selectTag) {
        return {
            id,
            title,
            type: 'select',
            multiple: extractSelectMultipleFromHeaderColumn(selectTag),
            options: extractOptionsFromHeaderColumn(selectTag),
        };
    }

    switch (type) {
        case 'number':
        case 'checkbox':
            return {
                id,
                title,
                type,
            };
        case 'rating':
            return {
                id,
                title,
                type,
                max: getNumberAttribute(td.properties, 'dataMax', 5),
            };
        case 'users':
            return {
                id,
                title,
                type,
                multiple: getBooleanAttribute(td.properties, 'dataMultiple'),
            };
        case 'content-ref':
            return {
                id,
                title,
                type,
            };
        case 'files':
            return {
                id,
                title,
                type,
            };
        case 'image':
            return {
                id,
                title,
                type,
            };
        default:
            return {
                id,
                title,
                type: 'text',
                textAlignment: getEnumAttribute(
                    td.properties,
                    'align',
                    ['left', 'center', 'right'],
                    'left'
                ),
                verticalAlignment: getEnumAttribute<TableTextColumnVerticalAlignment>(
                    td?.properties,
                    'vAlign',
                    [VerticalAlignment.Top, VerticalAlignment.Middle, VerticalAlignment.Bottom],
                    undefined
                ),
            };
    }
}

async function extractContentReferences(
    element: hast.Element,
    options: ParsingOptions,
    asFiles = false
): Promise<ContentRef[]> {
    const links: string[] = [];

    visit(
        element,
        // @ts-ignore
        (node) => node.type === 'element' && node.tagName === 'a' && node.properties.href,
        (node) => {
            // @ts-ignore
            links.push(node.properties.href);
        }
    );

    const contentReferences: ContentRef[] = [];

    await pMap(links, async (link) => {
        const contentReference = asFiles
            ? await options.resolveFile(link)
            : await options.resolveLink(link);

        if (contentReference) {
            contentReferences.push(contentReference);
        }
    });

    return contentReferences;
}

/**
 * Finds and returns a <select> tag in a <th>
 */
function getSelectFromHeaderColumn(th: hast.Element | null) {
    if (!th) {
        return null;
    }
    return th.children.find(
        (node) => node.type === 'element' && node.tagName === 'select'
    ) as hast.Element | null;
}

/**
 * Hydrates an array of DocumentTableSelectOption from a <select> in the header column
 */
function extractOptionsFromHeaderColumn(
    selectTag: hast.Element | null
): DocumentTableSelectOption[] {
    if (!selectTag) {
        return [];
    }

    return selectTag.children.map((option: hast.Element) => ({
        value: option.properties?.value as string,
        label: option.properties?.label as string,
        color: option.properties?.color as string,
    }));
}

function extractNumberValue(td: hast.Element): number | null {
    const text = extractNodeText(td).trim();
    if (!text) return null;
    const value = Number(text);
    return Number.isFinite(value) ? value : null;
}

/**
 * Extracts whether a select column allows multiple options to be selected or not.
 * To do so, we check whether the `multiple` attribute is set.
 */
function extractSelectMultipleFromHeaderColumn(selectTag: hast.Element | null): boolean {
    if (!selectTag) {
        return false;
    }

    return Boolean(selectTag?.properties?.multiple);
}
